import type { ColumnSizeState, GridState } from "ag-grid-community";

// field/colId of the static row-label column — the only pinned column that
// should keep a persisted fixed width.
const BENEFITS_LABEL_COLUMN_ID = "benefit.label";

/**
 * Drops persisted width/flex for pinned carrier columns (the Incumbent/
 * Requested plan column) so their size always follows the narrow/medium/wide
 * preset instead of a width saved from before that column supported scaling.
 * AG Grid restores an explicit width from `initialState` and disables flex
 * for that column for the life of the grid instance, so a stale entry would
 * otherwise permanently override the scale preset regardless of colDef.
 */
export const sanitizeQuotesGridState = (
  state: GridState | undefined,
): GridState | undefined => {
  const leftColIds = state?.columnPinning?.leftColIds;
  const sizingModel = state?.columnSizing?.columnSizingModel;
  if (!state || !leftColIds?.length || !sizingModel?.length) {
    return state;
  }

  const pinnedCarrierColIds = new Set(
    leftColIds.filter((colId) => colId !== BENEFITS_LABEL_COLUMN_ID),
  );
  if (pinnedCarrierColIds.size === 0) {
    return state;
  }

  return {
    ...state,
    columnSizing: {
      ...state.columnSizing,
      columnSizingModel: sizingModel.filter(
        (col: ColumnSizeState) => !pinnedCarrierColIds.has(col.colId),
      ),
    },
  };
};

/**
 * Drops column visibility from the mpx-persisted state: which plan columns are
 * hidden is kept per RFP in the browser (`useQuotesHiddenColumns`), and an
 * older visibility entry here would hide again, on every load, a column the
 * user has since shown. AG Grid treats `initialState` as complete unless it is
 * marked partial, and would then show a column defined with `hide: true`
 * because the state omits its visibility.
 */
export const omitColumnVisibility = (
  state: GridState | undefined,
): GridState | undefined => {
  if (!state) {
    return state;
  }
  return { ...state, columnVisibility: undefined, partialColumnState: true };
};
